import { Controller } from '@hotwired/stimulus';

const LOAD_ERROR_MESSAGE = '読み込みに失敗しました。';

// 条件フォームの送信は jquery_ujs の remote: true のまま残している
// その ajax:success は jQuery の trigger で発火して data-action では受け取れないため、パネルごとに fetch する
export default class extends Controller {
  static targets = ['content', 'loading'];

  static values = {
    // 表示時の条件（Analysis#pagination_params）をクエリに含めた URL
    url: String,
    form: String,
    // 読み込み前は中身が空なので、読み込んだ後に高さを確保する
    minHeight: String,
  };

  #abortController;

  connect() {
    this.#load(this.urlValue);
  }

  disconnect() {
    this.#abortController?.abort();
  }

  // 送信と「絞り込む」の無効化と復帰は jquery_ujs に任せるので、preventDefault しない
  reload({ target }) {
    if (target !== this.#form) return;

    this.#load(this.#urlWithConditions(target));
  }

  // ページネーションは読み込んだ中身に含まれ、差し替えるたびに作り直されるので、パネルで click を拾う
  paginate(event) {
    const link = event.target.closest('.p-pagination a');
    if (!link) return;

    event.preventDefault();
    this.#load(link.href);
  }

  async #load(url) {
    // 先に送ったリクエストの応答が後から届くと、新しい条件で読んだ中身を古い中身で上書きしてしまう
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    this.#toggleLoading(true);
    try {
      const response = await fetch(url, {
        headers: { Accept: 'text/html' },
        signal: abortController.signal,
      });
      if (!response.ok) {
        this.#content.textContent = LOAD_ERROR_MESSAGE;
        await this.#alertErrors(response);
        return;
      }
      this.#content.innerHTML = await response.text();
    } catch (error) {
      if (abortController.signal.aborted) return;

      console.error(error);
      this.#content.textContent = LOAD_ERROR_MESSAGE;
    } finally {
      // 中断したときは後から送ったリクエストが読み込み中なので、ローディングを消さない
      if (!abortController.signal.aborted) {
        this.#toggleLoading(false);
        this.element.style.minHeight = this.minHeightValue;
      }
    }
  }

  // submit を document で受ける時点では、shop-branch-ids-param が form で店舗をカンマ区切りの 1 つの値にまとめ終えている
  // 集客情報の条件フォームには期間の欄がないので、表示時の URL の期間を残したまま、フォームにある条件だけを上書きする
  #urlWithConditions(form) {
    const url = new URL(this.urlValue, window.location.origin);
    for (const [name, value] of new FormData(form)) {
      if (name.startsWith('analysis[')) url.searchParams.set(name, value);
    }
    return url.toString();
  }

  // 422 のときは { errors: [...] } が返る
  // パネルが複数あると同じエラーが並ぶので、既に出ていれば出さない
  async #alertErrors(response) {
    if (response.status !== 422) return;

    const { errors } = await response.json();
    if (document.querySelector('.alertify-notifier .ajs-message')) return;

    for (const error of errors) {
      alertify.error(error);
    }
  }

  #toggleLoading(loading) {
    if (!this.hasLoadingTarget) return;

    this.loadingTarget.classList.toggle('is-loaded', !loading);
  }

  get #form() {
    return document.querySelector(this.formValue);
  }

  get #content() {
    return this.hasContentTarget ? this.contentTarget : this.element;
  }
}
